// 신호 런타임의 React 바인딩 — **얇게 둔다**
//
// 판단은 전부 `src/web/audio/runtime.ts` 가 지고 여기는 그것을 화면의 생애주기에 잇기만
// 한다. `ClockSync` 와 `use-shell-clock.ts` 의 관계와 같다. 얇게 두는 이유는 하나다 —
// 이 층에서 가장 위험한 상황(기기가 자는 동안 오디오 시계만 멈춘다)을 단위 검사가 가짜
// 컨텍스트로 만들 수 있어야 하는데, 판단이 훅 안에 있으면 그 검사가 React 를 띄워야 한다.
//
// ── 매 틱 같은 것을 넘긴다 ──────────────────────────────────────────────────
//
// `update` 는 「지금 무엇을 골랐고 지금 몇 시인가」 둘만 받는 문이고, 화면은 그것을 매 틱
// 그대로 넘긴다. 「골랐다」·「바꿨다」·「시간이 흘렀다」를 각각의 이벤트로 보내지 않는다.
// 나누는 순간 어느 문으로 들어왔느냐에 따라 상태가 달라지는 자리가 생기고, 이 스텝이
// 없애려는 실패가 정확히 그 종류다.
//
// ── 시계가 다시 수렴하는 동안에도 그냥 넘긴다 ───────────────────────────────
//
// 기기가 자고 깨면 시계 동기가 모아 둔 표본을 통째로 버리고, 다시 셋을 모을 때까지
// `utcMillis` 가 **보정 없는 기기 시계**로 되돌아간다. 그동안 이 값은 offset 만큼 툭 튄다.
//
// 그것을 여기서 특별 취급하지 않는다. 튐은 「예약이 어긋났다」로 그대로 잡히고, 잡히면
// 다시 계산된다 — 어긋남을 **원인이 아니라 결과로** 재기로 한 설계가 이 세 번째 원인을
// 따로 알 필요 없이 덮는다. 그리고 화면의 카운트다운도 같은 `nowMs` 를 쓰므로, 시계가
// 틀린 동안에도 **귀와 눈이 같은 것을 말한다.** 둘이 어긋나면 사람이 무엇을 믿을지 고르게
// 되는데, 고르게 만드는 것 자체가 이 제품이 없애려는 혼동이다.

import { useCallback, useEffect, useState } from 'react'

import { type ViewStrings, view as viewStrings } from '../../../i18n/index.ts'
import { useLang } from '../../shell/use-lang.ts'
import {
  createAudioContext,
  SignalRuntime,
  type SignalObservations,
  type SignalStatus,
  type SignalTarget,
} from '../../audio/runtime.ts'

/**
 * Web Audio 가 없는 브라우저. 화면은 소리 없이 숫자만으로 서야 한다.
 *
 * **문장은 여기 없다** (2026-08-17). 이 자리에 한국어를 적어 두었더니 영어 화면에서도
 * 한국어로 떴고, 하필 **소리가 안 나는 순간**이라 사람이 가장 불안할 때 읽는 문장이다.
 * T6 개정이 오디오 런타임에서 사람 문장을 걷어냈는데 그 위층인 이 훅에 하나가 남아 있었다.
 * 상태는 여기가 정하고 글자는 카탈로그가 낸다.
 */
function noAudio(t: ViewStrings): SignalStatus {
  return { state: 'idle', reason: t.signalNoAudio, droppedMarkers: 0 }
}

const IDLE: SignalStatus = { state: 'idle', reason: '', droppedMarkers: 0 }

export interface UseSignalResult extends SignalStatus {
  /** **사람의 제스처 안에서 부른다.** 그 밖에서 부르면 브라우저가 잠김을 풀지 않는다. */
  arm: () => void
  observations: SignalObservations
  /**
   * 사람이 소리를 껐는가. **상태 유니온의 `'muted'` 와 따로 나른다.**
   *
   * 고른 것이 없으면 런타임의 상태는 `idle` 이라 `state` 만으로는 껐는지 알 수 없는데,
   * 껐다는 사실은 대상과 무관하게 참이다. 손잡이의 글자를 그 값으로 고르므로 둘이
   * 갈리면 버튼이 「켜기」라고 적힌 채 이미 켜져 있는 상태가 생긴다.
   */
  muted: boolean
  /** **사람의 제스처 안에서 부른다** (켤 때 잠김도 함께 푼다). */
  toggleMuted: () => void
  /**
   * 이 브라우저가 소리를 낼 수 있는가. 없으면 끄는 손잡이도 뜻이 없으므로 화면이
   * 손잡이를 그리지 않는다. 눌러도 아무 일이 없는 버튼은 고장으로 읽힌다.
   */
  hasAudio: boolean
}

export function useSignal(input: {
  target: SignalTarget | null
  /** 자기 동기 시계의 지금. **화면의 카운트다운과 같은 값이어야 한다.** */
  nowMs: number
}): UseSignalResult {
  const [runtime] = useState<SignalRuntime | null>(() => {
    const ctx = createAudioContext()
    return ctx === null ? null : new SignalRuntime({ context: ctx })
  })
  const t = viewStrings(useLang().lang)
  // **소리 없는 상태를 저장하지 않고 파생시킨다.** `useState` 의 초기값은 한 번만
  // 계산되므로 거기 문장을 넣으면 언어를 바꿔도 그 한 줄만 처음 언어로 얼어붙는다 —
  // 모듈 최상단 상수가 import 시점에 얼어붙던 것과 자리만 다른 같은 버그다. 아래 효과가
  // 런타임이 없을 때 일찍 돌아가므로 이 값은 어차피 갱신될 자리도 아니다.
  const [status, setStatus] = useState<SignalStatus>(IDLE)
  const [observations, setObservations] = useState<SignalObservations>({
    missedCount: 0,
    rescheduleCount: 0,
  })
  /**
   * 껐는가의 거울. **정본이 새로고침을 넘기지 않기로 했으므로 초기값은 언제나 꺼짐이다**
   * (2026-09-02 개정). 저장소에서 읽어 오는 자리가 없는 것이 그 결정의 구현이고, 여기
   * 한 줄을 `localStorage` 로 바꾸는 순간 정본과 어긋난다.
   *
   * 소유는 런타임에 있고 이것은 그리기 위한 거울이다. 아래 매 틱 효과가 이 값을 건드리지
   * 않는 것이 중요하다. 거기서 되읽으면 사람이 누른 순간과 다음 틱 사이에 한 프레임짜리
   * 옛 값이 보인다.
   */
  const [muted, setMuted] = useState(false)

  // 원시값으로 의존성을 적는다. 대상 객체를 그대로 적으면 매 렌더가 새 객체를 만들어
  // 효과가 끝없이 다시 돈다.
  const rallyId = input.target?.rallyId ?? null
  const openMs = input.target?.openMs ?? null
  const { nowMs } = input

  useEffect(() => {
    if (runtime === null) return
    const next = runtime.update({
      target: rallyId === null || openMs === null ? null : { rallyId, openMs },
      serverNowMs: nowMs,
    })
    // 바뀐 것이 없으면 같은 객체를 그대로 둔다. 새 객체를 매 틱 넣으면 화면이 그때마다
    // 다시 그려지고, 그 다시 그리기가 소리 예약을 건드리는 자리까지 흔든다.
    setStatus((prev) =>
      prev.state === next.state &&
      prev.reason === next.reason &&
      prev.droppedMarkers === next.droppedMarkers
        ? prev
        : next,
    )
    setObservations((prev) => {
      const now = runtime.observations
      return prev.missedCount === now.missedCount && prev.rescheduleCount === now.rescheduleCount
        ? prev
        : now
    })
  }, [runtime, rallyId, openMs, nowMs])

  // 이 화면을 떠나면 걸어 둔 소리도 걷는다. 안 걷으면 다른 화면에서 **아무 이름도 없이**
  // 카운트 소리가 울린다 — 「이름 없는 카운트다운은 존재하지 않는다」의 소리 쪽 짝이다.
  useEffect(() => {
    if (runtime === null) return
    return () => {
      runtime.dispose()
    }
  }, [runtime])

  const arm = useCallback(() => {
    if (runtime === null) return
    void runtime.arm().then(setStatus)
  }, [runtime])

  /**
   * 소리를 끄거나 켠다. **켤 때 같은 제스처 안에서 잠김을 함께 푼다.**
   *
   * 나눠 부르면 껐다 켠 사람이 잠김 상자를 한 번 더 눌러야 한다. 새로고침 직후와 iOS 에서
   * 오디오 컨텍스트가 잠긴 채로 있는 것이 기본이라 그 두 번 누르기가 예외가 아니라 흔한
   * 경로이고, 두 번째 버튼이 뜨기 전까지 사람은 자기가 켠 소리가 왜 안 나는지 모른다.
   *
   * **브라우저가 제스처를 하나로 보는 것이 중요하다.** `arm` 은 사용자 제스처 안에서
   * 불러야 잠김이 풀리는데, 여기서 `setMuted` 뒤에 곧바로 이어 부르므로 같은 클릭
   * 핸들러 안이다. 상태를 먼저 바꾸고 다음 틱을 기다렸다 부르면 그 조건이 깨진다.
   */
  const toggleMuted = useCallback(() => {
    if (runtime === null) return
    const next = !runtime.muted
    const settled = runtime.setMuted(next)
    setMuted(next)
    if (next) {
      setStatus(settled)
      return
    }
    void runtime.arm().then(setStatus)
  }, [runtime])

  return {
    ...(runtime === null ? noAudio(t) : status),
    arm,
    observations,
    muted,
    toggleMuted,
    hasAudio: runtime !== null,
  }
}
